import produce from "immer";
import { includes, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { isValid as isValidNumber } from "@tootools/number";
import { getFocusesFromClause } from "@radarly/shared/cards/facets";
import { Clause } from "../clause";
import { getFieldsFromClause } from "../clause/getter";
import { refineClauseWithInnerClause } from "../clause/refiner";
import { isMultipleLineChartDataviz } from "../dataviz";
import {
  AIFragment,
  EmptyFragment,
  GlobalFragment,
  SearchFragment,
  SearchFragmentDateTerm,
  SearchFragmentPivot,
  SearchFragmentPosts,
  SearchFragmentTerm,
  SearchFragmentWithAgg,
  getTermFromAggs,
  isAIFragment,
  isClusterFragment,
  isEmptyFragment,
  isInfluencerFragment,
  isSearchFragmentDateTerm,
  isSearchFragmentPivot,
  isSearchFragmentPosts,
  isSearchFragmentTerm,
} from "../search";
import { DateTermCard } from ".";

function hasFocusesClause(clause: Clause) {
  return includes(getFieldsFromClause(clause), Facet.Focuses);
}

function getQueryWithCtx(clause: Clause, workspaceId: number) {
  if (hasFocusesClause(clause) || workspaceId === null) {
    return clause;
  }
  return refineClauseWithInnerClause(clause, {
    dashboard: workspaceId,
  });
}

function updateAggsWithFctx(
  fragment: SearchFragmentWithAgg,
  workspaceId: number
) {
  if (isSearchFragmentDateTerm(fragment)) {
    return getSearchFragmentDateTermAggsWithFctx(fragment, workspaceId);
  }
  if (isSearchFragmentPivot(fragment)) {
    return getSearchFragmentPivotAggsWithFctx(fragment, workspaceId);
  }
  if (isSearchFragmentTerm(fragment)) {
    return getSearchFragmentTermAggsWithFctx(fragment, workspaceId);
  }
  return fragment.request.aggs;
}

function updateAggsWithFocuses(
  fragment: SearchFragmentWithAgg,
  focuses: number[]
) {
  if (isSearchFragmentDateTerm(fragment)) {
    return getSearchFragmentDateTermAggsWithFocuses(fragment, focuses);
  }
  if (isSearchFragmentPivot(fragment)) {
    return getSearchFragmentPivotAggsWithFocuses(fragment, focuses);
  }
  if (isSearchFragmentTerm(fragment)) {
    return getSearchFragmentTermAggsWithFocuses(fragment, focuses);
  }
  return fragment.request.aggs;
}

function getSearchFragmentPivotAggsWithFctx(
  fragment: SearchFragmentPivot,
  workspaceId: number
) {
  const pivot = getFirstAggTermFromFragment(fragment);
  const against = getSecondAggTermFromFragment(fragment, pivot);
  return produce(fragment.request.aggs, (draft) => {
    if (isFocusesTerm(pivot)) {
      draft[pivot].dashboard = workspaceId;
    }
    if (isFocusesTerm(against)) {
      draft[pivot].aggs[against].dashboard = workspaceId;
    }
  });
}

function getSearchFragmentPivotAggsWithFocuses(
  fragment: SearchFragmentPivot,
  focuses: number[]
) {
  const pivot = getFirstAggTermFromFragment(fragment);
  const against = getSecondAggTermFromFragment(fragment, pivot);
  return produce(fragment.request.aggs, (draft) => {
    if (isFocusesTerm(pivot)) {
      draft[pivot].focuses = focuses;
    }
    if (isFocusesTerm(against)) {
      draft[pivot].aggs[against].focuses = focuses;
    }
  });
}

function getSearchFragmentTermAggsWithFctx(
  fragment: SearchFragmentTerm,
  workspaceId: number
) {
  const term = getFirstAggTermFromFragment(fragment);
  return produce(fragment.request.aggs, (draft) => {
    if (isFocusesTerm(term)) {
      draft[term].dashboard = workspaceId;
    }
  });
}

function getSearchFragmentTermAggsWithFocuses(
  fragment: SearchFragmentTerm,
  focuses: number[]
) {
  const term = getFirstAggTermFromFragment(fragment);
  return produce(fragment.request.aggs, (draft) => {
    if (isFocusesTerm(term)) {
      draft[term].focuses = focuses;
      delete draft[term].dashboard;
    }
  });
}

function getSearchFragmentDateTermAggsWithFctx(
  fragment: SearchFragmentDateTerm,
  workspaceId: number
) {
  return produce(fragment.request.aggs, (draft) => {
    if (getSecondAggTermFromFragment(fragment, "date") === Facet.Focuses) {
      draft.date.aggs.focuses.dashboard = workspaceId;
    }
  });
}

function getSearchFragmentDateTermAggsWithFocuses(
  fragment: SearchFragmentDateTerm,
  focuses: number[]
) {
  return produce(fragment.request.aggs, (draft) => {
    if (getSecondAggTermFromFragment(fragment, "date") === Facet.Focuses) {
      draft.date.aggs.focuses.focuses = focuses;
      delete draft.date.aggs.focuses.dashboard;
    }
  });
}

function isFocusesTerm(term: string) {
  return term === Facet.Focuses;
}

function getFirstAggTermFromFragment(fragment: SearchFragmentWithAgg) {
  return getTermFromAggs(fragment.request.aggs);
}

function getSecondAggTermFromFragment(
  fragment: SearchFragmentWithAgg,
  aggTerm: string
) {
  return getTermFromAggs(fragment.request.aggs[aggTerm].aggs);
}

export function getFragmentWithContext<
  T extends GlobalFragment | EmptyFragment
>(fragment: T, workspaceId: number, projectFocuses: number[]) {
  if (isEmptyFragment(fragment)) {
    return null;
  }
  if (isInfluencerFragment(fragment)) {
    return fragment;
  }
  const isGlobalInsightPageContext = isValidNumber(workspaceId) ? false : true;
  if (isGlobalInsightPageContext) {
    const focuses = getFocusesFromClause(fragment.request.query);
    return getValidFragmentWithFocusesCtx(
      fragment,
      focuses.length > 0 ? focuses : projectFocuses
    );
  }
  return getValidFragmentWithWorkspaceIdCtx(fragment, workspaceId);
}

function getValidFragmentWithWorkspaceIdCtx<
  T extends SearchFragment | AIFragment
>(fragment: T, workspaceId: number): T {
  if (
    isSearchFragmentPosts(fragment) ||
    isClusterFragment(fragment) ||
    isAIFragment(fragment)
  ) {
    return getSearchFragmentPostsWithWorkspaceIdCtx(fragment, workspaceId) as T;
  }
  return getSearchFragmentWithAggWithWorkspaceIdCtx(fragment, workspaceId) as T;
}

function getValidFragmentWithFocusesCtx<T extends SearchFragment | AIFragment>(
  fragment: T,
  focuses: number[]
): T {
  if (isSearchFragmentPosts(fragment) || isAIFragment(fragment)) {
    return fragment;
  }
  return getSearchFragmentWithAggWithFocusesCtx(fragment, focuses) as T;
}

function getSearchFragmentPostsWithWorkspaceIdCtx(
  fragment: SearchFragmentPosts | AIFragment,
  workspaceId: number
) {
  const query = getQueryWithCtx(fragment.request.query, workspaceId);
  return produce(fragment, (draft) => {
    draft.request.query = query;
  });
}

function getSearchFragmentWithAggWithWorkspaceIdCtx(
  fragment: SearchFragmentWithAgg,
  workspaceId: number
) {
  const query = getQueryWithCtx(fragment.request.query, workspaceId);
  return produce(fragment as SearchFragmentWithAgg, (draft) => {
    draft.request.query = query;
    draft.request.aggs = updateAggsWithFctx(fragment, workspaceId);
  });
}

function getSearchFragmentWithAggWithFocusesCtx(
  fragment: SearchFragmentWithAgg,
  focuses: number[]
) {
  return produce(fragment as SearchFragmentWithAgg, (draft) => {
    draft.request.aggs = updateAggsWithFocuses(fragment, focuses);
  });
}

// In DateTerm's MultipleLineChart card, we can use hashtags and keyphrases.
// Due to technical requests from the backend team, we can't directly inject hashtags and keyphrases into the clause when editing the card.
// Ergo, hashtags and keyphrases filters are saved in MultipleLineChart's dataviz, and only sent to fragment's request when performing the final api call.
// This peculiar case is only valid when using a DateTerm's MultipleLineChart card alongside hashtags or keyphrases facet.
export const getValidDateTermFragment = (
  card: DateTermCard,
  term: Facet,
  fragment: SearchFragmentDateTerm<[Metric]>
): SearchFragmentDateTerm => {
  if (
    isMultipleLineChartDataviz(card.dataviz) &&
    (term === Facet.Hashtags || term === Facet.Keyphrases)
  ) {
    const filteredValues = map(card.dataviz.filteredValues, "value").filter(
      Boolean
    );
    if (filteredValues.length === 0) {
      return null;
    }
    const formattedValues =
      term === Facet.Hashtags
        ? filteredValues.map((v) => v.replace("#", ""))
        : filteredValues;
    const cleanClause: Clause = refineClauseWithInnerClause(
      fragment.request.query,
      { [term]: formattedValues },
      true
    );
    const request = fragment.request;
    return Object.assign({}, fragment, {
      request: {
        ...request,
        query: cleanClause,
        aggs: {
          ...request.aggs,
          date: {
            ...request.aggs.date,
            aggs: {
              ...request.aggs.date.aggs,
              [term]: {
                ...request.aggs.date.aggs[term],
                include: formattedValues,
              },
            },
          },
        },
      },
    });
  }
  return fragment;
};
